import { useState, useEffect } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Input,
  Tabs,
} from "antd";


import apiClient from "@/utils/apiClient";
import {
  SearchOutlined,
  DownloadOutlined,
  PlusOutlined,
  BankOutlined,
  DollarCircleOutlined,
  FileTextOutlined
} from "@ant-design/icons";
import { invoiceService } from "@/services/invoiceService";
import AdvancedCustomInvoiceModal from "../components/AdvancedCustomInvoiceModal";
import SalaryAccountsManagement from "../components/SalaryAccountsManagement";
import HotelPaymentsManagement from "../components/HotelPaymentsManagement";
import TransportPaymentsManagement from "../components/TransportPaymentsManagement";

const { Text } = Typography;
const { Search } = Input;
const AccountsManagement = () => {
  const [activeTab, setActiveTab] = useState("booking_accounts");
  const [customInvoiceModalVisible, setCustomInvoiceModalVisible] = useState(false);
  const [selectedInvoiceBooking, setSelectedInvoiceBooking] = useState(null);

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showLedger, setShowLedger] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const openLedger = (booking) => {
    setSelectedBooking(booking);
    setShowLedger(true);
  };
  const openSupplierLedger = (supplier) => {
    setSelectedBooking(supplier);
    setShowLedger(true);
  };

const [
  pendingPayments,
  setPendingPayments
] = useState([]);

  useEffect(() => {
    fetchAccounts();
    fetchInvoices();
    fetchPendingPayments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [accounts, searchQuery, statusFilter]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);

      const res = await apiClient.get("/bookings");

      const bookings = res.data.data || [];

      setAccounts(bookings);
      setFilteredAccounts(bookings);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      message.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

const fetchPendingPayments =
async () => {

  try {

    const res =
      await apiClient.get(
        "/accounting/pending-payments"
      );

    setPendingPayments(
      res.data?.data || []
    );

  } catch (err) {

    console.error(err);
  }
};

const verifyPayment =
async (payment) => {

  try {

    await apiClient.patch(

`/accounting/leads/${payment.lead._id}/payments/${payment._id}`,

      {
        verificationStatus:
          "verified"
      }
    );

    message.success(
      "Payment verified"
    );

    fetchPendingPayments();
    await fetchAccounts();

  } catch (err) {

    message.error(
      "Verification failed"
    );
  }
};

const rejectPayment =
async (payment) => {

  try {

    await apiClient.patch(

`/accounting/leads/${payment.lead._id}/payments/${payment._id}`,

      {
        verificationStatus:
          "rejected"
      }
    );

    message.success(
      "Payment rejected"
    );

    fetchPendingPayments();
    await fetchAccounts();

  } catch (err) {

    message.error(
      "Rejection failed"
    );
  }
};

  const openInvoiceEditor = (booking) => {
    setSelectedInvoiceBooking(booking || null);
    setCustomInvoiceModalVisible(true);
  };

  const fetchInvoices = async () => {
    try {
      setInvoiceLoading(true);
      const res = await invoiceService.getAll();
      setInvoices(
        Array.isArray(res?.data)
        ? res.data
        : []
    );
    } catch (err) {
      console.error("Invoice fetch error:", err);
      message.error("Failed to load invoices");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = accounts;

    // 🔍 SEARCH
    if (searchQuery) {
filtered = filtered.filter((b) => {

  const q = searchQuery.toLowerCase();

return (

(b.customer?.name || "")
.toLowerCase()
.includes(q)

||

(b.customer?.phone || "")
.toLowerCase()
.includes(q)

||

(b.customer?.leadId || "")
.toLowerCase()
.includes(q)

||

(b.bookingReference || "")
.toLowerCase()
.includes(q)

||

(b.destination?.city || "")
.toLowerCase()
.includes(q)

);

});

    }

    // 🔥 STATUS FILTER (MAIN FIX)
    if (statusFilter !== "all") {
      filtered = filtered.filter((b) => {
        const balance = b.financials?.balanceDue || 0;

        // customer side
        if (statusFilter === "paid") return balance === 0;
        if (statusFilter === "pending") return balance > 0;


        return true;
      });
    }

    setFilteredAccounts(filtered);
  };

  const handleDeleteAccount = async () => {
    message.info("Delete not supported");
  };

  const handleExportAccounts = () => {
    try {
      const doc = new jsPDF();

      const data = getSupplierData();

      // Header
      doc.setFontSize(16);
      doc.text("Supplier Accounts Report", 14, 20);

      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 26);

      // Table
      const tableData = data.map((s) => {
        const due = s.totalCost - s.totalPaid;

        return [
          s.supplierName,
          `₹${s.totalCost.toLocaleString()}`,
          `₹${s.totalPaid.toLocaleString()}`,
          `₹${due.toLocaleString()}`,
          due > 0 ? "Pending" : "Paid",
        ];
      });

      autoTable(doc, {
        startY: 30,
        head: [["Supplier", "Total Cost", "Paid", "Due", "Status"]],
        body: tableData,
      });

      doc.save("supplier_accounts.pdf");

      message.success("PDF exported");
    } catch (err) {
      console.error(err);
      message.error("PDF export failed");
    }
  };

  const generateFinalInvoice = async (data) => {
    try {
      let subtotal = 0;

      // ✅ CORRECT ITEMS STRUCTURE
      const items = data.hotels.map((h) => {
        const total = (h.overridePrice || 0) * (h.nights || 1);
        subtotal += total;

        return {
          description: h.hotelName || "Hotel",
          quantity: h.nights || 1,
          unitPrice: h.overridePrice || 0,
          totalPrice: total,
          taxRate: data.taxPercent || 0,
          taxAmount: 0,
        };
      });

      // transport
      subtotal += data.transportCost || 0;

      const taxAmount = (subtotal * (data.taxPercent || 0)) / 100;
      const totalAmount = subtotal + taxAmount;

      // ✅ BACKEND PAYLOAD (MATCHES MODEL)
      const payload = {
        bookingId: data._id,
        customerId: data.customerId || data._id, // temp fallback

        bookingReference: data.bookingReference,
        customerName: data.customerName,

        items,

        subtotal,
        taxAmount,
        totalAmount,

        currency: "INR",

        status: "draft",
        paymentStatus: "pending",

        notes: "Generated from system",
        terms: "Standard terms apply",
      };

      // ✅ SAVE TO BACKEND
      const res = await apiClient.post("/invoices", payload);

      // ✅ PDF GENERATION (same data)
      const doc = new jsPDF();

      doc.text("INVOICE", 14, 20);

      autoTable(doc, {
        startY: 30,
        head: [["Item", "Qty", "Price", "Total"]],
        body: items.map((i) => [
          i.description,
          i.quantity,
          `₹${i.unitPrice}`,
          `₹${i.totalPrice}`,
        ]),
      });

      const finalY = doc.lastAutoTable.finalY + 10;

      doc.text(`Subtotal: ₹${subtotal}`, 14, finalY);
      doc.text(`Tax: ₹${taxAmount}`, 14, finalY + 6);
      doc.text(`Total: ₹${totalAmount}`, 14, finalY + 12);

      doc.save(`invoice_${data.bookingReference}.pdf`);

      message.success("Invoice saved & downloaded");

      setInvoiceModal(false);
    } catch (err) {
      console.error(err);
      message.error("Invoice failed");
    }
  };

  const addPayment = async (hotel, payment) => {
    try {
      await apiClient.post("/payments", {
        hotelId: hotel._id,
        bookingId: hotel.bookingId,
        amount: payment.amount,
        method: payment.method,
      });

      message.success("Payment recorded");
      fetchAccounts();
    } catch (err) {
      message.error("Payment failed");
    }
  };


  const columns = [

{
  title: "Lead",
render: (b) => (

  <div>

    <div
      style={{
        color: "#1677ff",
        fontWeight: 600
      }}
    >
      {b.customer?.leadId || "-"}
    </div>

    <div
      style={{
        color: "#888",
        fontSize: 12
      }}
    >
      {b.bookingReference}
    </div>

    <Tag
      color={
        b.bookingStage === "booked"
          ? "green"
          : b.bookingStage === "in_progress"
          ? "blue"
          : b.bookingStage === "past"
          ? "default"
          : "orange"
      }
    >
      {b.bookingStage || "new"}
    </Tag>

  </div>

),

},

{
  title: "Customer",
  render: (b) => (
    <div>
      <div style={{ fontWeight: 600 }}>
        {b.customer?.name || "-"}
      </div>

      <div
        style={{
          fontSize: 12,
          color: "#888"
        }}
      >
        {b.customer?.phone || ""}
      </div>
    </div>
  ),
},

{
  title: "Sales Owner",

  render: (b) => (

    <div>

      <div
        style={{
          fontWeight: 600
        }}
      >
        {b.teams?.sales || "-"}
      </div>

    </div>

  ),

},

{
  title: "Basic Details",

render: (b) => (

  <div>

    <div style={{ fontWeight: 600 }}>
      {b.destination?.city || "-"}
    </div>

    <div
      style={{
        fontSize: 12,
        color: "#888"
      }}
    >
      {b.bookingType || "-"}
    </div>

    <div
      style={{
        fontSize: 11,
        color: "#999"
      }}
    >
      {new Date(b.departureDate).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      })}
      {" → "}
      {new Date(b.returnDate).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      })}
      {" • "}
      {b.duration || 0}D
    </div>

  </div>

),

},

{
  title:"Start",

  render:(b)=>

    new Date(
      b.departureDate
    ).toLocaleDateString(
      "en-IN",
      {
        day:"2-digit",
        month:"short"
      }
    )
},

{
  title:"End",

  render:(b)=>

    new Date(
      b.returnDate
    ).toLocaleDateString(
      "en-IN",
      {
        day:"2-digit",
        month:"short"
      }
    )
},

    {
      title: "Revenue",
      render: (b) =>
  `₹${(
    b.computedFinancials?.grandTotal ||    
    b.pricing?.grandTotal ||
    b.pricing?.totalPrice ||
    0
  ).toLocaleString()}`,
    },
    {
      title: "Received",
      render: (b) => `₹${(b.financials?.totalPaid || 0).toLocaleString()}`,
    },
    {
      title: "Pending",
      render: (b) => `₹${(b.financials?.balanceDue || 0).toLocaleString()}`,
    },

{
  title: "Supplier Cost",
  render: (b) =>
    `₹${(
      b.computedFinancials?.totalCost ||
      b.pricing?.supplierCost ||
      0
    ).toLocaleString()}`,
},

{
  title: "Profit",
  render: (b) => {

    const profit =
      b.computedFinancials?.margin ??
      (
        (
          b.computedFinancials?.grandTotal ||
          b.pricing?.grandTotal ||
          b.pricing?.totalPrice ||
          0
        ) -
        (
          b.computedFinancials?.totalCost ||
          b.pricing?.supplierCost ||
          0
        )
      );

    return (
      <span style={{ color: profit >= 0 ? "green" : "red" }}>
        ₹{profit.toLocaleString()}
      </span>
    );
  },
},

];

  // 🔥 FINANCIAL CALCULATIONS (CORRECT PLACE)


const totalRevenue = (accounts || []).reduce(
  (sum, b) =>
    sum +
    (
      b.computedFinancials?.grandTotal ||
      b.pricing?.grandTotal ||
      b.pricing?.totalPrice ||
      0
    ),
  0,
);

  const totalReceived = (accounts || []).reduce(
    (sum, b) => sum + (b.financials?.totalPaid || 0),
    0,
  );

const totalCost = (accounts || []).reduce(
  (sum, b) =>
    sum +
    (
      b.computedFinancials?.totalCost ||
      b.pricing?.supplierCost ||
      0
    ),
  0,
);

const totalOutstanding =
  (accounts || []).reduce(
    (sum, b) =>
      sum + (b.financials?.balanceDue || 0),
    0
  );

const totalProfit =
  totalRevenue - totalCost;

const marginPercent =
  totalRevenue > 0
    ? (totalProfit / totalRevenue) * 100
    : 0;

  const invoiceColumns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
    },
    {
      title: "Customer",
      render: (_, r) => r.customerName || "N/A",
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      render: (v) => `₹${v}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => (
        <Tag color={s === "paid" ? "green" : "orange"}>{s?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (d) => new Date(d).toLocaleDateString(),
    },
    {
      title: "Action",
      render: (_, r) => (
        <Button
          size="small"
          onClick={async () => {
            try {
              const blob = await invoiceService.download(r._id);

              const url = window.URL.createObjectURL(new Blob([blob]));
              const link = document.createElement("a");
              link.href = url;
              link.download = `${r.invoiceNumber}.pdf`;
              link.click();
            } catch (err) {
              message.error("Download failed");
            }
          }}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      {/* Header Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Accounts & Financial Management</Typography.Title>
          <Text type="secondary">Overview of booking revenues, pending payment verifications, staff salary accounts, and custom customer invoices</Text>
        </div>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedInvoiceBooking(null);
              setCustomInvoiceModalVisible(true);
            }}
            style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
          >
            Generate Custom Invoice
          </Button>
        </Space>
      </div>

      {/* Main Tabs Header */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        style={{ marginBottom: 20 }}
        items={[
          {
            key: "booking_accounts",
            label: (
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                📊 Booking Accounts & Payments
              </span>
            ),
          },
          {
            key: "salary_accounts",
            label: (
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                💼 Employee Salary Accounts & Payroll
              </span>
            ),
          },
          {
            key: "hotel_payments",
            label: (
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                🏨 Hotel Payments & Liabilities
              </span>
            ),
          },
          {
            key: "transport_payments",
            label: (
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                🚖 Transport Payments & Vendors
              </span>
            ),
          },
          {
            key: "invoices",
            label: (
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                📄 Customer Invoices & History
              </span>
            ),
          },
        ]}
      />

      {activeTab === "salary_accounts" && (
        <SalaryAccountsManagement />
      )}

      {activeTab === "hotel_payments" && (
        <HotelPaymentsManagement />
      )}

      {activeTab === "transport_payments" && (
        <TransportPaymentsManagement />
      )}

      {activeTab === "invoices" && (
        <Card
          title="Customer Invoices & Custom Billing"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedInvoiceBooking(null);
                setCustomInvoiceModalVisible(true);
              }}
              style={{ background: "#4f46e5", borderColor: "#4f46e5" }}
            >
              Create New Custom Invoice
            </Button>
          }
        >
          <Table
            columns={invoiceColumns}
            dataSource={invoices}
            loading={invoiceLoading}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
      )}

      {activeTab === "booking_accounts" && (
      <Row gutter={16}>

{/* 🔥 LEFT SIDEBAR - PRO VERSION */}
<Col span={5}>
  <Card
    style={{
      borderRadius: 14,
      padding: 14,
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    }}
  >
    {/* Header */}
    <div
      style={{
        fontWeight: 600,
        fontSize: 14,
        marginBottom: 12,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>Filters</span>
      <span style={{ fontSize: 11, color: "#999" }}>Status</span>
    </div>

    {/* Filter List */}
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {[
        { key: "all", label: "All", color: "#555" },
        { key: "pending", label: "Customer Pending", color: "#faad14" },
        { key: "paid", label: "Customer Paid", color: "#1677ff" },
      ].map((item) => {
        const active = statusFilter === item.key;

        return (
          <div
            key={item.key}
            onClick={() => setStatusFilter(item.key)}
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",

              background: active ? "#f0f7ff" : "transparent",
              border: active
                ? "1px solid #91caff"
                : "1px solid transparent",

              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (!active)
                e.currentTarget.style.background = "#fafafa";
            }}
            onMouseLeave={(e) => {
              if (!active)
                e.currentTarget.style.background = "transparent";
            }}
          >
            {/* LEFT */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              
              {/* Dot Indicator */}
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: item.color,
                }}
              />

              <span
                style={{
                  fontWeight: active ? 600 : 400,
                  color: active ? "#1677ff" : "#444",
                }}
              >
                {item.label}
              </span>
            </div>

            {/* RIGHT INDICATOR */}
            {active && (
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#1677ff",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  </Card>
</Col>


        {/* 🔥 RIGHT CONTENT */}

        {/* 🔥 FINANCIAL DASHBOARD */}
        <Col span={19}>
          <Row gutter={16} style={{ marginBottom: 24 }}>

<Col span={4}>
  <Card>
    <Statistic
      title="Revenue"
      value={totalRevenue}
      prefix="₹"
    />
  </Card>
</Col>

<Col span={4}>
  <Card>
    <Statistic
      title="Received"
      value={totalReceived}
      prefix="₹"
    />
  </Card>
</Col>

<Col span={4}>
  <Card>
    <Statistic
      title="Outstanding"
      value={totalOutstanding}
      prefix="₹"
      valueStyle={{
        color: totalOutstanding > 0
          ? "#fa8c16"
          : "#52c41a",
      }}
    />
  </Card>
</Col>

<Col span={4}>
  <Card>
    <Statistic
      title="Cost"
      value={totalCost}
      prefix="₹"
    />
  </Card>
</Col>

<Col span={4}>
  <Card>
    <Statistic
      title="Profit"
      value={totalProfit}
      prefix="₹"
      valueStyle={{
        color: totalProfit >= 0
          ? "#52c41a"
          : "#ff4d4f",
      }}
    />
  </Card>
</Col>

<Col span={4}>
  <Card>
    <Statistic
      title="Margin %"
      value={marginPercent.toFixed(1)}
      suffix="%"
      valueStyle={{
        color: marginPercent >= 20
          ? "#52c41a"
          : "#fa8c16",
      }}
    />
  </Card>
</Col>

          </Row>

<Card
  title="Pending Payment Verification"
  style={{ marginBottom: 24 }}
>
  <Table
    rowKey="_id"
    dataSource={pendingPayments}
    pagination={false}
    columns={[
      {
        title: "Lead",
        render: (_, r) =>
          r.lead?.customerName ||
          r.lead?.leadId ||
          "-"
      },

      {
        title: "Amount",
        render: (_, r) =>
          `₹${(
            r.amount || 0
          ).toLocaleString()}`
      },

      {
        title: "Type",
        dataIndex: "paymentType"
      },

{
  title: "Mode",
  render: (_, r) =>
    r.paymentMode || "-"
},

{
  title: "UTR",
  render: (_, r) =>
    r.utrNumber || "-"
},

      {
        title: "Logged",
        render: (_, r) =>
          new Date(
            r.createdAt
          ).toLocaleDateString()
      },

      {
        title: "Action",

        render: (_, r) => (
          <Space>

            <Button
              type="primary"
              size="small"
              onClick={() =>
                verifyPayment(r)
              }
            >
              Verify
            </Button>

            <Button
              danger
              size="small"
              onClick={() =>
                rejectPayment(r)
              }
            >
              Reject
            </Button>

          </Space>
        )
      }
    ]}
  />
</Card>

          {/* Filters and Actions */}

          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col span={8}>
                <Search
                  placeholder="Search customer, lead id or booking..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>

              <Col span={16}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                  }}
                >
                  {/* 🔥 TEST INVOICE BUTTON (YAHI ADD KIYA HAI) */}
                  <Button
                    onClick={() => {
                      if (accounts[0]) openInvoiceEditor(accounts[0]);
                    }}
                  >
                    Generate Invoice
                  </Button>

                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleExportAccounts}
                  >
                    Export
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Accounts Table */}

<Card>
  <Table
    columns={columns}
    dataSource={filteredAccounts}
    loading={loading}
    rowKey="_id"
    pagination={{
      pageSize: 10,
      showSizeChanger: true,
      showQuickJumper: true,
    }}
  />
</Card>


          {/* 🔥 INVOICE HISTORY */}
          <Card title="Invoice History" style={{ marginTop: 24 }}>
            <Table
              columns={invoiceColumns}
              dataSource={invoices}
              loading={invoiceLoading}
              rowKey="_id"
              pagination={{
                pageSize: 5,
              }}
            />
          </Card>

          {showLedger && selectedBooking && (
            <div className="modal-overlay">
              <Card
                title={`Supplier Ledger - ${selectedBooking.supplierName}`}
                style={{ maxWidth: 800, margin: "auto" }}
              >
                {selectedBooking.bookings?.map((item, idx) => {
                  const hotel = item.hotel;

                  const total =
                    (hotel.pricePerNight || 0) * (hotel.nights || 1);

                  const payments = hotel.payments || [];

                  const paid =
                    payments.length > 0
                      ? payments.reduce((sum, p) => sum + (p.amount || 0), 0)
                      : hotel.paidAmount || 0;

                  const due = total - paid;

                  return (
                    <Card key={idx} size="small" style={{ marginBottom: 12 }}>
                      <Row gutter={16}>
                        <Col span={6}>
                          <Text strong>
                            {hotel.hotelName || hotel.name || "Hotel"}
                          </Text>
                          <br />
                          <Text type="secondary">
                            Booking: {item.bookingReference}
                          </Text>

                          {/* 🔥 INVOICE BUTTON (YAHI ADD KIYA HAI) */}
                          <div style={{ marginTop: 6 }}>
                            <Button
                              size="small"
                              onClick={() =>
                                openInvoiceEditor({
                                  ...selectedBooking,
                                  bookingReference: item.bookingReference,
                                  hotelReservations: [hotel],
                                })
                              }
                            >
                              Invoice
                            </Button>
                          </div>
                        </Col>

                        <Col span={4}>
                          <Text>Total</Text>
                          <div>₹{total.toLocaleString()}</div>
                        </Col>

                        <Col span={4}>
                          <Text>Paid</Text>
                          <div style={{ color: "green" }}>
                            ₹{paid.toLocaleString()}
                          </div>
                        </Col>

                        <Col span={4}>
                          <Text>Due</Text>
                          <div style={{ color: "red" }}>
                            ₹{due.toLocaleString()}
                          </div>
                        </Col>

                        <Col span={4}>
                          <Tag color={due > 0 ? "orange" : "green"}>
                            {due > 0 ? "Pending" : "Paid"}
                          </Tag>
                        </Col>
                      </Row>

                      {/* 🔥 TRANSACTIONS */}
                      <div style={{ marginTop: 12 }}>
                        <Text strong>Payments:</Text>
                        {hotel.payments && hotel.payments.length > 0 ? (
                          hotel.payments.map((p, i) => (
                            <div
                              key={i}
                              style={{
                                fontSize: 12,
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span>₹{(p.amount || 0).toLocaleString()}</span>
                              <span>
                                {p.date
  ? new Date(p.date).toLocaleDateString()
  : "-"}
                              </span>
                              <span>{p.method || "manual"}</span>
                            </div>
                          ))
                        ) : (
                          <div style={{ fontSize: 12, color: "#999" }}>
                            No payments recorded
                          </div>
                        )}
                      </div>
                      <Button
                        size="small"
                        style={{ marginTop: 8 }}
                        onClick={() => {
                          const amount = prompt("Enter payment amount:");
                          const method = prompt(
                            "Payment method (cash/bank/upi):",
                          );

                          if (amount) {
                            addPayment(hotel, {
                              amount: Number(amount),
                              method: method || "manual",
                            });
                          }
                        }}
                      >
                        Add Payment
                      </Button>
                    </Card>
                  );
                })}

                {/* ✅ CLOSE BUTTON (INSIDE OUTER CARD) */}
                <div style={{ textAlign: "right", marginTop: 16 }}>
                  <Button onClick={() => setShowLedger(false)}>Close</Button>
                </div>
              </Card>
            </div>
          )}
        </Col>
        {invoiceModal && invoiceData && (
          <div className="modal-overlay">
            <Card
              title={`Invoice - ${invoiceData.bookingReference}`}
              style={{ maxWidth: 900, margin: "auto" }}
            >
              {invoiceData.hotels.map((h, i) => (
                <Row key={i} gutter={16} style={{ marginBottom: 10 }}>
                  <Col span={8}>{h.hotelName}</Col>

                  <Col span={6}>
                    <Input
                      value={h.overridePrice}
                      onChange={(e) => {
                        const updated = [...invoiceData.hotels];
                        updated[i].overridePrice = Number(e.target.value || 0)
                        setInvoiceData({ ...invoiceData, hotels: updated });
                      }}
                    />
                  </Col>

                  <Col span={4}>x {h.nights || 1}</Col>
                </Row>
              ))}

              {/* TRANSPORT */}
              <div style={{ marginTop: 10 }}>
                Transport:
                <Input
                  type="number"
                  value={invoiceData.transportCost}
                  onChange={(e) =>
                    setInvoiceData({
                      ...invoiceData,
                      transportCost: Number(e.target.value || 0)
                    })
                  }
                />
              </div>

              {/* TAX */}
              <div style={{ marginTop: 10 }}>
                Tax %:
                <Input
                  value={invoiceData.taxPercent}
                  onChange={(e) =>
                    setInvoiceData({
                      ...invoiceData,
                      taxPercent: Number(e.target.value || 0)
                    })
                  }
                />
              </div>

              {/* ACTIONS */}
              <div style={{ marginTop: 20, textAlign: "right" }}>
                <Button
                  onClick={() => generateFinalInvoice(invoiceData)}
                  type="primary"
                >
                  Download PDF
                </Button>

                <Button
                  onClick={() => setInvoiceModal(false)}
                  style={{ marginLeft: 8 }}
                >
                  Close
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* 🔥 CLOSE RIGHT PANEL */}
      </Row>
      )}

      {/* Advanced Custom Customer Invoice Modal */}
      <AdvancedCustomInvoiceModal
        visible={customInvoiceModalVisible}
        onClose={() => setCustomInvoiceModalVisible(false)}
        initialData={selectedInvoiceBooking}
        onSuccess={() => {
          fetchInvoices();
        }}
      />
    </div>
  );
};

export default AccountsManagement;
